/* eslint-disable complexity */
import {
  AbilityBuilder,
  ForcedSubject,
  Ability,
  AbilityClass,
} from '@casl/ability';

import { UserRoleEnum } from '../types';

const actions = [
  'manage',
  'create',
  'edit',
  'cancel',
  'read',
  'printFlow',
] as const;
const subjects = [
  'Employees',
  'Dashboard',
  'Absences',
  'Candidates',
  'Candidate',
  'Events',
  'EventTypes',
  'EventBatch',
  'PaperEvents',
  'User',
  'Review',
  'Policy',
  'Organization',
  'Competencies',
  'all',
  'Settings',
  'CompanyProfiles',
  'SettingsAdmin',
  'SettingsAdmin',
  'Attorneys',
  'Vacations',
  'VacationsSettings',
  'Payslips',
  'Personal',
  'SmevErrors',
  'GeneralNews',
  'SocialServices',
  'CompanyServices',
  'EmployeeServices',
  'News',
  'Quiz',
  'Categories',
  'SocialAccess',
  'Review',
] as const;

export type AppAbilities = [
  (typeof actions)[number],
  (
    | (typeof subjects)[number]
    | ForcedSubject<Exclude<(typeof subjects)[number], 'all'>>
  ),
];

export type Resources = AppAbilities[1];
export type AppAbility = Ability<AppAbilities>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AppAbility = Ability as AbilityClass<AppAbility>;

export type AbilityConfig = {
  role: string;
  userRepresentative: boolean;
  canSeeEvents: boolean;
  canSeeEventsToPaper: boolean;
  canSeeEmployees: boolean;
  canSeePolicies: boolean;
  canSeeCompany: boolean;
  canSeeCompanyProfiles: boolean;
  canSeeSettings: boolean;
  canSeeCandidates: boolean;
  canSeeAttorneys: boolean;
  canSeeVacations: boolean;
  canSeeVacationsSettings: boolean;
  canSeePayslips: boolean;
  canSeeGoalManagement: boolean;
  canSeeSmevErrors: boolean;
  canSeeAbsences: boolean;
  canSeeCompetencies: boolean;
  canSeePersonalProfile: boolean;
  canSeeUnits: boolean;
  canSeeNews: boolean;
  canSeeSocialCategories: boolean;
  canSeeSocialAccess: boolean;
  canSeeSocialSurveys: boolean;
};

export default function defineAbilityFor({
  role,
  userRepresentative,
  canSeeEvents,
  canSeeEventsToPaper,
  canSeeEmployees,
  canSeePolicies,
  canSeeCompany,
  canSeeCompanyProfiles,
  canSeeSettings,
  canSeeCandidates,
  canSeeAttorneys,
  canSeeVacations,
  canSeeVacationsSettings,
  canSeePayslips,
  canSeeGoalManagement,
  canSeeSmevErrors,
  canSeeAbsences,
  canSeeCompetencies,
  canSeePersonalProfile,
  canSeeUnits,
  canSeeNews,
  canSeeSocialCategories,
  canSeeSocialAccess,
  canSeeSocialSurveys,
}: AbilityConfig) {
  const { can, cannot, build } = new AbilityBuilder(AppAbility);

  can('manage', 'all');

  if (role === UserRoleEnum.Employee) {
    cannot('read', 'Employees');
    cannot('cancel', 'Events');
    cannot('create', 'EventTypes');
    cannot('read', 'Candidates');
    cannot('create', 'Candidates');
    cannot('read', 'PaperEvents');
    cannot('create', 'Policy');
    cannot('edit', 'Policy');
    cannot('create', 'Organization');
    cannot('edit', 'Organization');
    cannot('read', 'Settings');
    cannot('read', 'CompanyProfiles');
    cannot('read', 'CompanyServices');
    cannot('read', 'Attorneys');
    cannot('read', 'SettingsAdmin');
    cannot('printFlow', 'Events');
    cannot('read', 'News');
  }

  if (role === UserRoleEnum.Company) {
    cannot('read', 'Competencies');
  }

  if (!canSeeUnits) {
    cannot('read', 'Organization');
  }

  if (!canSeeEmployees) {
    cannot('read', 'Employees');
  }

  if (!canSeeEvents) {
    cannot('read', 'Events');
  }

  if (!canSeeEventsToPaper) {
    cannot('read', 'PaperEvents');
  }

  if (!canSeePolicies) {
    cannot('read', 'Policy');
  }

  if (!canSeeCompany) {
    cannot('create', 'Organization');
    cannot('edit', 'Organization');
  }

  if (!canSeeCandidates) {
    cannot('read', 'Candidates');
    cannot('create', 'Candidates');
  }

  if (!canSeeVacations) {
    cannot('read', 'Vacations');
  }

  if (!canSeeVacationsSettings) {
    cannot('read', 'VacationsSettings');
  }

  if (!canSeeSmevErrors) {
    cannot('read', 'SmevErrors');
  }

  if (!canSeePayslips) {
    cannot('read', 'Payslips');
  }

  if (!canSeeGoalManagement) {
    cannot('read', 'Review');
  }

  if (role === UserRoleEnum.Company) {
    cannot('read', 'Personal');
  }

  if (canSeeSettings || canSeeAttorneys || canSeeVacationsSettings) {
    can('read', 'Settings');

    if (!canSeeSettings) {
      cannot('read', 'SettingsAdmin');
    }

    if (!canSeeAttorneys) {
      cannot('read', 'Attorneys');
    }

    if (!canSeeVacationsSettings) {
      cannot('read', 'VacationsSettings');
    }
  } else {
    cannot('read', 'Settings');
    cannot('read', 'CompanyProfiles');
    cannot('read', 'SettingsAdmin');
    cannot('read', 'Attorneys');
    cannot('read', 'SettingsAdmin');
    cannot('read', 'VacationsSettings');
  }

  if (!canSeeCompanyProfiles) {
    cannot('read', 'CompanyProfiles');
  }

  if (!canSeeAbsences) {
    cannot('read', 'Absences');
  }

  if (!canSeeCompetencies) {
    cannot('read', 'Competencies');
  }

  if (!canSeePersonalProfile) {
    cannot('read', 'Personal');
  }

  if (
    canSeeNews ||
    canSeeSocialCategories ||
    canSeeSocialSurveys ||
    canSeeSocialAccess
  ) {
    can('read', 'SocialServices');

    if (!canSeeNews) {
      cannot('read', 'News');
      cannot('read', 'GeneralNews');
    }

    if (!canSeeSocialCategories) {
      cannot('read', 'Categories');
    }

    if (!canSeeSocialAccess) {
      cannot('read', 'SocialAccess');
    }

    if (role === UserRoleEnum.Employee || !canSeeSocialSurveys) {
      cannot('read', 'Quiz');
    }
  } else {
    cannot('read', 'SocialServices');
    cannot('read', 'News');
    cannot('read', 'GeneralNews');
    cannot('read', 'Quiz');
    cannot('read', 'Categories');
    cannot('read', 'SocialAccess');
  }

  if (role === UserRoleEnum.Employee || !canSeeSocialSurveys) {
    cannot('read', 'Quiz');
  }

  if (window.REACT_APP_VKHRTEK_LK_DISABLED && !userRepresentative) {
    cannot('manage', 'all');
    cannot('read', 'Settings');
  }

  return build();
}
